import { memo } from 'react';

import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { getPublicHeadlineFontFamilyValue, usePublicHeadlineFont } from '~/hooks/usePublicHeadlineFont';

export type PublicHeaderTitleProps = {
  title: string;
  dataTestId: string;
};

export const PublicHeaderTitle = memo(({ title, dataTestId }: PublicHeaderTitleProps) => {
  const { headlineColor } = usePublicCustomColors();
  const headlineFont = usePublicHeadlineFont();

  // Compose the two optional headline treatments; stays `undefined` (no style attr at all) when
  // neither is set, so links without them render byte-identically to before. The weight and style
  // come from the font's chosen variant so the title applies the one face the loader requested —
  // this title declares no weight of its own, so an absent variant leaves it inheriting the header
  // slot's 600 exactly as before, while a chosen variant makes Regular actually render regular
  // (inheriting 600 against a regular-only load is what renders as synthesized fake bold) and
  // Italic actually render italic.
  const headlineStyle =
    headlineColor || headlineFont
      ? {
          color: headlineColor || undefined,
          fontFamily: headlineFont ? getPublicHeadlineFontFamilyValue(headlineFont.fontFamily) : undefined,
          fontWeight: headlineFont?.fontWeight,
          fontStyle: headlineFont?.fontStyle,
        }
      : undefined;

  return (
    <h1
      className="flex h-10 max-w-[650px] items-center px-2 ads:text-primary text-24 text-gray-12"
      data-disableselect={true}
      data-testid={dataTestId}
      style={headlineStyle}
    >
      <span className="truncate">{title}</span>
    </h1>
  );
});

PublicHeaderTitle.displayName = 'PublicHeaderTitle';
